import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  MenuItem,
  Alert,
} from "@mui/material";
import { useCashOutV2 } from "@/hooks/useCashOutV2";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { CashOutFormV2 } from "@/components/cashOut/CashOutFormV2";
import { ErrorDisplay } from "@/components/common/ErrorDisplay";
import { UserSelector } from "@/components/cashOut/UserSelector";
import { VENUES, type Venue } from "@restaurant-staff-portal/shared";

export function CashOutCreatePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { cashOutPermissions, isManagerOrAbove } = usePermissions();
  const { createCashOut, isCreating } = useCashOutV2();
  const [venue, setVenue] = useState<Venue>("THE_BULLWHEEL");
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const handleSubmit = async (data: any) => {
    try {
      const createData: any = {
        ...data,
        venue,
      };
      
      // Add selected user if manager/admin selected a different user
      if (isManagerOrAbove && selectedUserId) {
        createData.onBehalfOfUserId = selectedUserId;
      }
      
      await createCashOut(createData);
      navigate("/cash-outs");
    } catch (err) {
      setError(err as Error);
    }
  };

  const handleCancel = () => {
    navigate("/cash-outs");
  };

  if (!user) {
    return null;
  }

  if (!cashOutPermissions.canCreate) {
    return (
      <Container maxWidth="lg">
        <ErrorDisplay
          error={new Error("You do not have permission to create cash outs")}
        />
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Create Cash Out
        </Typography>

        {/* User selector for ADMIN/MANAGER only */}
        {isManagerOrAbove && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <UserSelector
              value={selectedUserId}
              onChange={setSelectedUserId}
              label="Create Cash Out For User"
              helperText="Select a user to create a cash out on their behalf. Leave empty to create for yourself."
            />
            {selectedUserId && (
              <Alert severity="info" sx={{ mt: 2 }}>
                You are creating a cash out on behalf of another user. The 4-hour restriction will not apply.
              </Alert>
            )}
          </Paper>
        )}

        <Paper sx={{ p: 3, mb: 3 }}>
          <TextField
            select
            fullWidth
            label="Select Venue"
            value={venue}
            onChange={(e) => setVenue(e.target.value as Venue)}
            sx={{ mb: 3 }}
          >
            {Object.entries(VENUES).map(([key, label]) => (
              <MenuItem key={key} value={key}>
                {label}
              </MenuItem>
            ))}
          </TextField>
        </Paper>

        {error && <ErrorDisplay error={error} />}

        <CashOutFormV2
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isLoading={isCreating}
          venue={venue}
        />
      </Box>
    </Container>
  );
}
